<script setup lang="ts">
import { LucideLandmark } from 'lucide-vue-next'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import { RATE_PLACEHOLDER } from '~/data/pages/rates/consts'

const { t } = useI18n()

const props = withDefaults(defineProps<{ currencies?: string[]; isNbkr?: boolean }>(), {
  currencies: () => ['usd', 'eur', 'rub', 'kzt', 'cny'],
  isNbkr: false
})

const currencyList = useCurrencyStore().list
const { data } = await useFetch('/api/rates/widgets-cached')
type RateKey = 'averageRatesToday' | 'bestRatesToday' | 'centralBankRates'

const tabs: Record<RateKey, string> = {
  averageRatesToday: t('common.average_rates_today'),
  bestRatesToday: t('common.best_rates_today'),
  centralBankRates: t('common.nbkr')
}

const exchangeRates = computed(() => {
  return (Object.entries(tabs) as [RateKey, string][]).map(([key, name]) => {
    const rates = (data.value?.[key] ?? {}) as Record<string, number | undefined>

    const ratesFormatted = props.currencies.map((currency) => {
      const upperCurrency = currency.toUpperCase()

      const currencyInfo = currencyList.find((i) => i.code === upperCurrency)
      const currencyId = currencyInfo?.id ?? null

      const format = (value: number | undefined) => {
        if (!currencyId) return RATE_PLACEHOLDER

        return useCurrencyFormat(value, currencyId, false) ?? RATE_PLACEHOLDER
      }

      // The central bank table has a single rate per currency instead of buy/sell.
      const sellValue = key === 'centralBankRates' ? rates[currency] : rates[`sell_${currency}`]

      return {
        title: upperCurrency,
        buy: format(rates[`buy_${currency}`]),
        sell: format(sellValue),
        image: currency
      }
    })

    return {
      value: key,
      name,
      rates: ratesFormatted
    }
  })
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <Tabs :default-value="props.isNbkr ? 'centralBankRates' : 'averageRatesToday'">
      <TabsList v-if="!props.isNbkr" class="grid w-full gap-0 grid-cols-3">
        <TabsTrigger
          v-for="item in exchangeRates"
          :key="item.value"
          :value="item.value"
          class="border-none"
        >
          <LucideLandmark v-if="item.value === 'centralBankRates'" class="size-5" />
          {{ item.name }}
        </TabsTrigger>
      </TabsList>

      <TabsContent v-for="item in exchangeRates" :key="item.value" :value="item.value">
        <table class="mt-2 w-full">
          <thead>
            <tr>
              <th class="py-1.5 text-left text-xs font-normal leading-3 text-gray-500">
                {{ $t('widget.sidebar_rates.table_headers.currency') }}
              </th>
              <th class="py-1.5 text-right text-xs font-normal leading-3 text-gray-500">
                {{
                  item.value === 'centralBankRates'
                    ? ''
                    : $t('widget.sidebar_rates.table_headers.buy')
                }}
              </th>
              <th class="py-1.5 text-right text-xs font-normal leading-3 text-gray-500">
                {{
                  item.value === 'centralBankRates'
                    ? $t('widget.sidebar_rates.table_headers.rate')
                    : $t('widget.sidebar_rates.table_headers.sell')
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rate in item.rates" :key="rate.title">
              <td class="py-2">
                <div class="text-md flex gap-2 font-semibold">
                  <CurrencyIcon :code="rate.image" size="lg" />
                  {{ rate.title }}
                </div>
              </td>
              <td class="text-right">{{ item.value === 'centralBankRates' ? '' : rate.buy }}</td>
              <td class="text-right">{{ rate.sell }}</td>
            </tr>
          </tbody>
        </table>
      </TabsContent>
    </Tabs>
    <NuxtLinkLocale class="text-blue-600 hover:text-blue-700" to="/rates">{{
      $t('widget.sidebar_rates.view_all_rates')
    }}</NuxtLinkLocale>
  </div>
</template>
